// LnsLandingPage.tsx
import React, { useState, useEffect, memo, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Divider,
  Link,
  IconButton,
  InputBase,
  Tooltip,
  Fab,
} from "@mui/material";
import { useThemeMode } from "../contexts/ThemeContext";
import {
  Phone,
  Email,
  LocationOn,
  CalendarToday,
  Gavel,
  AccountBalance,
  ArrowForward,
  Star,
  ReceiptLong,
  Business,
  Description,
  Search as SearchIcon,
  WhatsApp,
  Map as MapIcon,
  ArrowUpward,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import ModernHeader from "../components/ModernHeader";

// Simple motion variant used across sections
const fadeUp = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

// Lightweight placeholder for the animated background previously used.
// Keeps layout and accessibility stable while avoiding heavy DOM elements.
const AnimatedBackground = ({ mode }) => {
  return null;
};
AnimatedBackground.displayName = "AnimatedBackground";

// ========================================
// STATIC SERVICES DATA
// ========================================
const PRADEEP_SERVICES = [
  "STAMP PAPERS",
  "ALL TYPE OF AGREEMENTS",
  "AFFIDAVITS",
  "NOTARY SERVICES",
  "MARRIAGE CERTIFICATE",
  "PROPERTY REGISTRATIONS",
  "GAZETTED SIGN",
];

const VIJAY_SERVICES = [
  "PAN & TAN APPLICATIONS",
  "GST REGISTRATION",
  "INCOME TAX FILINGS",
  "PF REGISTRATION",
  "GST MONTHLY RETURNS",
  "PT REGISTRATION",
  "GST AUDITS",
  "ESI REGISTRATION",
  "TDS FILINGS & PAYMENTS",
  "BOOK KEEPING SERVICES",
  "GST ANNUAL RETURNS (9 & 9C)",
  "AUDITOR SERVICES",
  "GST REFUND PROCESS",
  "COMPANY INCORPORATION",
  "NETWORTH CERTIFICATE (CA CERTIFICATE)",
  "PARTNERSHIP, LLP REGISTRATION",
  "IEC CODE & ICEGATE REGISTRATION",
  "MSME REGISTRATIONS",
  "ROC FILINGS",
  "TRADE & FOOD LICENCES",
  "ALL TYPES OF ACCOUNTING SERVICES",
  "UDYAM & LABOUR CERTIFICATE REGISTRATION",
  "VIRTUAL CFO",
  "INTERNAL TAX AUDITS",
];


// ========================================
// UTILS
// ========================================
const pickIcon = (label) => {
  const t = String(label).toLowerCase();
  if (/(stamp|agreement|affidavit|notary|marriage|gazett)/.test(t)) return Gavel;
  if (/(gst|income tax|tds|pf|esi)/.test(t)) return AccountBalance;
  if (/(return|filing|audit)/.test(t)) return ReceiptLong;
  if (/(registration|incorporation|licen|msme|roc|iec|icegate|pan|tan|udyam)/.test(t)) return Business;
  if (/(certificate|networth)/.test(t)) return Description;
  return ArrowForward;
};
const normalize = (s) => s.toLowerCase().replace(/\s+/g, " ").trim();
const highlightMatch = (text, query, mode) => {
  if (!query) return text;
  const i = normalize(text).indexOf(normalize(query));
  if (i === -1) return text;
  const highlightColor = "#52B788";
  return (
    <>
      {text.slice(0, i)}
      <mark
        style={{
          background: "transparent",
          color: highlightColor,
          fontWeight: 700,
        }}
      >
        {text.slice(i, i + query.length)}
      </mark>
      {text.slice(i + query.length)}
    </>
  );
};

// ========================================
// HERO
// ========================================
const HeroSection = memo(({ mode }) => {
  return (
    <motion.div variants={fadeUp} initial="initial" whileInView="animate" viewport={{ once: true, amount: 0.2 }}>
      <Box
        sx={{
          borderRadius: 5,
          overflow: "hidden",
          mb: 5,
          background: mode === "dark"
            ? "linear-gradient(135deg, rgba(82,183,136,0.08) 0%, rgba(149,213,178,0.08) 100%)"
            : "linear-gradient(135deg, rgba(82,183,136,0.08) 0%, rgba(149,213,178,0.08) 100%)",
          border: "1px solid rgba(82,183,136,0.12)",
          boxShadow: mode === "dark"
            ? "0 8px 32px rgba(0,0,0,0.4)"
            : "0 16px 40px rgba(30,64,175,0.08)",
        }}
      >
        <Box
          sx={{
            position: "relative",
            p: { xs: 3, sm: 4, md: 8 },
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1.5,
              px: { xs: 2, sm: 3 },
              py: { xs: 1, sm: 1.25 },
              borderRadius: "999px",
              background:
                mode === "dark"
                  ? "rgba(45,106,79,0.15)"
                  : "rgba(45,106,79,0.15)",
              border:
                mode === "dark"
                  ? "1px solid rgba(45,106,79,0.35)"
                  : "1px solid rgba(45,106,79,0.35)",
              mb: { xs: 2, sm: 3 },
            }}
            role="note"
          >
            <Star sx={{ fontSize: { xs: 14, sm: 16 }, color: "#2D6A4F" }} />
            <Typography
              variant="caption"
              sx={{
                color: "#2D6A4F",
                fontWeight: 700,
                letterSpacing: "0.06em",
                fontSize: { xs: "0.7rem", sm: "0.8rem" },
              }}
            >
              TRUSTED LEGAL & TAX EXPERTS
            </Typography>
          </Box>

          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: 900,
              mb: { xs: 2, sm: 2.5 },
              fontSize: { xs: "2rem", sm: "2.5rem", md: "4.5rem" },
              color: "#2D6A4F",
              lineHeight: { xs: 1.2, md: 1.1 },
              px: { xs: 1, sm: 0 },
            }}
          >
            L.N.S. & ASSOCIATES
          </Typography>

          <Typography
            variant="h5"
            sx={{
              mb: { xs: 3, sm: 4 },
              fontSize: { xs: "1rem", sm: "1.2rem", md: "1.75rem" },
              fontWeight: 800,
              color: "#2D6A4F",
              textTransform: "uppercase",
              letterSpacing: { xs: "0.08em", sm: "0.12em" },
              px: { xs: 1, sm: 0 },
            }}
          >
            Legal & Tax Consultants
          </Typography>

          <Typography
            variant="body1"
            sx={{
              maxWidth: "850px",
              mx: "auto",
              fontSize: { xs: "0.95rem", sm: "1rem", md: "1.2rem" },
              lineHeight: { xs: 1.6, sm: 1.8 },
              color: "#52B788",
              fontWeight: 600,
              px: { xs: 1, sm: 0 },
            }}
          >
            Expert assistance in Legal, Taxation, Registration, and Documentation with fast turnaround and trusted guidance.
          </Typography>
        </Box>
      </Box>
    </motion.div>
  );
});
HeroSection.displayName = "HeroSection";

// ========================================
// PERSON CARD (with click-to-convert)
// ========================================
const PersonCard = memo(({ name, subtitle, accent = "#52B788", services = [], searchTerm = "", onServiceClick, mode }) => {
  const filtered = useMemo(() => {
    const q = normalize(searchTerm);
    if (!q) return services;
    return services.filter((s) => normalize(s).includes(q));
  }, [services, searchTerm]);

  return (
    <motion.div variants={fadeUp} initial="initial" whileInView="animate" viewport={{ once: true, amount: 0.12 }}>
      <Card
        elevation={0}
        sx={{
          height: "100%",
          background: "rgba(255,255,255,0.95)",
          border: `1px solid ${accent}26`,
          transition: "transform .3s ease, box-shadow .3s ease, border-color .3s ease",
          "&:hover": {
            transform: "translateY(-6px)",
            borderColor: accent,
            boxShadow: `0 14px 38px ${accent}33`,
          },
        }}
        role="region"
        aria-label={`${name} services`}
      >
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          {/* Header */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                background: `${accent}22`,
                border: `1px solid ${accent}44`,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
              aria-hidden
            >
              <Star sx={{ fontSize: 22, color: accent }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: "-0.01em" }}>
                {name}
              </Typography>
              {subtitle && (
                <Typography variant="body2" sx={{ color: mode === "dark" ? "#A1AFC9" : "#5B6B7F", mt: 0.25 }}>
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Box>

          <Divider sx={{ my: 2, borderColor: `${accent}20` }} />

          {/* Services grid (clickable for booking) */}
          <Grid container spacing={{ xs: 1, sm: 1.25 }} role="list">
            {filtered.length === 0 && (
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
                  No matching services.
                </Typography>
              </Grid>
            )}

            {filtered.map((s, i) => {
              const Icon = pickIcon(s);
              return (
                <Grid key={i} item xs={12} sm={6} md={4} role="listitem">
                  <Box
                    onClick={() => onServiceClick?.(s, name)}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                      p: 1.25,
                      borderRadius: 2,
                      border: mode === "dark" ? `1px solid rgba(255,255,255,0.06)` : `1px solid rgba(0,0,0,0.06)`,
                      background: mode === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)",
                      transition: "transform .2s ease, background .2s ease, border-color .2s ease",
                      cursor: onServiceClick ? "pointer" : "default",
                      "&:hover": {
                        background: onServiceClick ? `${accent}15` : mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
                        borderColor: `${accent}55`,
                        transform: "translateY(-2px)",
                        boxShadow: onServiceClick ? `0 4px 12px ${accent}30` : "none",
                      },
                    }}
                    role={onServiceClick ? "button" : undefined}
                    aria-label={onServiceClick ? `Book ${s} with ${name}` : undefined}
                    tabIndex={onServiceClick ? 0 : undefined}
                    onKeyDown={(e) => {
                      if (onServiceClick && (e.key === "Enter" || e.key === " ")) {
                        e.preventDefault();
                        onServiceClick(s, name);
                      }
                    }}
                  >
                    <Icon sx={{ fontSize: 18, mt: "2px", color: accent, flexShrink: 0 }} />
                    <Typography
                      variant="body2"
                      sx={{ lineHeight: 1.5, color: mode === "dark" ? "inherit" : "#1f2937" }}
                    >
                      {highlightMatch(s, searchTerm || "", mode)}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>
    </motion.div>
  );
});
PersonCard.displayName = "PersonCard";

// ========================================
// CONTACT CARD
// ========================================
const ContactCard = memo(
  ({ icon: Icon, label, value, href, color, mode }) => {
    return (
      <Box
        component="a"
        href={href}
        sx={{
          display: "flex",
          alignItems: "center",
          p: 3.5,
          borderRadius: 3,
          background: mode === "dark" ? "rgba(10,10,10,0.75)" : "rgba(255,255,255,0.95)",
          border: `1px solid ${color}40`,
          textDecoration: "none",
          transition: "all 0.3s ease",
          "&:hover": {
            background: "rgba(255,255,255,1)",
            borderColor: color,
            transform: "translateY(-4px)",
            boxShadow: `0 10px 32px ${color}40`,
          },
        }}
        aria-label={`${label} ${value}`}
      >
        <Box sx={{ p: 2, borderRadius: 2, background: `${color}20`, mr: 2.5 }} aria-hidden>
          <Icon sx={{ fontSize: 28, color }} />
        </Box>
        <Box>
          <Typography variant="body1" sx={{ fontWeight: 700, mb: 0.25, color: "#2D6A4F" }}>
            {label}
          </Typography>
          <Typography variant="body2" sx={{ color, fontWeight: 600 }}>
            {value}
          </Typography>
        </Box>
      </Box>
    );
  }
);
ContactCard.displayName = "ContactCard";

// ========================================
// MOBILE ACTION BAR (kept; independent of service click)
// ========================================
const MobileActionBar = ({
  onBook,
  onCall1,
  onCall2,
  onWhatsApp1,
  onWhatsApp2,
  onMap,
  mode,
}) => {
  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 12,
        left: 0,
        right: 0,
        zIndex: 1300,
        px: 2,
        display: { xs: "flex", md: "none" },
        justifyContent: "center",
      }}
      role="group"
      aria-label="Quick actions"
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(5, 1fr)",
          gap: 1,
          width: "100%",
          maxWidth: 700,
          background: "rgba(255,255,255,0.95)",
          border: "1px solid rgba(0,0,0,0.08)",
          borderRadius: 16,
          padding: 1,
          backdropFilter: "blur(12px)",
        }}
      >
        <Tooltip title="Call Pradeep" arrow>
          <IconButton onClick={onCall1} aria-label="Call Pradeep" color="primary">
            <Phone />
          </IconButton>
        </Tooltip>
        <Tooltip title="WhatsApp Pradeep" arrow>
          <IconButton onClick={onWhatsApp1} aria-label="WhatsApp Pradeep" color="primary">
            <WhatsApp />
          </IconButton>
        </Tooltip>
        <Tooltip title="Book Slot" arrow>
          <IconButton onClick={onBook} aria-label="Book Slot" color="primary">
            <CalendarToday />
          </IconButton>
        </Tooltip>
        <Tooltip title="WhatsApp Vijay" arrow>
          <IconButton onClick={onWhatsApp2} aria-label="WhatsApp Vijay" color="primary">
            <WhatsApp />
          </IconButton>
        </Tooltip>
        <Tooltip title="Call Vijay" arrow>
          <IconButton onClick={onCall2} aria-label="Call Vijay" color="primary">
            <Phone />
          </IconButton>
        </Tooltip>
      </Box>
      <Fab color="primary" sx={{ position: "fixed", right: 16, bottom: 76 }} onClick={onMap} aria-label="Open map">
        <MapIcon />
      </Fab>
    </Box>
  );
};

// ========================================
// MAIN
// ========================================
export default function LnsLandingPage() {
  const { mode, toggleMode } = useThemeMode();
  const navigate = useNavigate();
  const handleBooking = useMemo(() => () => navigate("/booking"), [navigate]);
  
  // Handle navigation to services section
  const handleServicesClick = useCallback(() => {
    if (window.location.pathname !== '/') {
      navigate('/#services');
    } else {
      const servicesElement = document.getElementById('services');
      if (servicesElement) {
        servicesElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        navigate('/#services');
      }
    }
  }, [navigate]);
  
  // Handle navigation to contact section
  const handleContactClick = useCallback(() => {
    if (window.location.pathname !== '/') {
      navigate('/#contact');
    } else {
      const contactElement = document.getElementById('contact');
      if (contactElement) {
        contactElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        navigate('/#contact');
      }
    }
  }, [navigate]);
  
  // Handle service click - navigate to booking with query params (prefilled form)
  const handleServiceClick = useMemo(
    () => (service, personName) => {
      // Navigate to booking page with query parameters
      const personParam = personName.includes("PRADEEP") ? "pradeep" : "vijay";
      navigate(`/booking?service=${encodeURIComponent(service)}&person=${personParam}`);
    },
    [navigate]
  );

  const address =
    "OLD POST OFFICE ROAD, TSSP 8TH BATTALION, PRASHANTH NAGAR COLONY, KONDAPUR, HYDERABAD, TELANGANA – 500084";
  const googleMapsUrl = useMemo(
    () => `https://maps.app.goo.gl/EW8NpjTvtYDUxoiK6?g_st=ic`,
    []
  );

  const [query, setQuery] = useState("");
  const servicesRef = useRef(null);

  // quick actions (kept)
  const call = (num) => window.open(`tel:${num}`, "_self");
  const toWhatsApp = (num, text) =>
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");

  // back to top
  const [showTop, setShowTop] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <>
      <AnimatedBackground mode={mode} />
      <ModernHeader 
        onBookingClick={handleBooking}
        onServicesClick={handleServicesClick}
        onContactClick={handleContactClick}
        mode={mode} 
        toggleMode={toggleMode} 
      />

      <Box 
        component="main" 
        sx={{ 
          minHeight: "100vh", 
          pt: 6, 
          pb: 14, 
          position: "relative", 
          zIndex: 1,
          backgroundColor: "#ffffff",
        }}
      >
        <Container 
          maxWidth="lg"
          sx={{
            backgroundColor: "#ffffff",
          }}
        >
          <HeroSection mode={mode} />

          {/* Services + Search */}
          <Box id="services" ref={servicesRef} sx={{ scrollMarginTop: "88px", mb: 3 }}>
            <motion.div variants={fadeUp} initial="initial" whileInView="animate" viewport={{ once: true, amount: 0.2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 0.5, sm: 1 },
                  p: { xs: 0.75, sm: 1 },
                  borderRadius: 3,
                  border: "1px solid rgba(0,0,0,0.10)",
                  background: "rgba(255,255,255,0.9)",
                  mb: { xs: 2, sm: 3 },
                }}
                role="search"
                aria-label="Search services"
              >
                <SearchIcon sx={{ mx: { xs: 1, sm: 1.5 }, fontSize: { xs: 20, sm: 24 }, color: "text.secondary" }} />
                <InputBase
                  placeholder="Search services…"
                  inputProps={{ "aria-label": "Search services" }}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  sx={{ 
                    flex: 1, 
                    py: { xs: 1, sm: 1.2 }, 
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                    fontWeight: 600, 
                    letterSpacing: "0.01em" 
                  }}
                />
                {query && (
                  <Button 
                    onClick={() => setQuery("")} 
                    aria-label="Clear search"
                    size="small"
                    sx={{ minWidth: { xs: "auto", sm: "64px" }, px: { xs: 1, sm: 2 } }}
                  >
                    Clear
                  </Button>
                )}
              </Box>
            </motion.div>

            <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
              <Grid item xs={12} md={6}>
                <PersonCard
                  name="Services Available:"
                  subtitle=""
                  accent="#52B788"
                  services={PRADEEP_SERVICES}
                  searchTerm={query}
                  onServiceClick={handleServiceClick}
                  mode={mode}
                />
                {/* Three quote boxes below Pradeep's services with green styling */}
                <Box sx={{ mt: 3, width: '100%' }}>
                  <Grid container spacing={2}>
                    {/* Box 1: Main quote */}
                    <Grid item xs={12}>
                      <Box sx={{
                        p: 2.5,
                        borderRadius: 2,
                        background: '#ffffff',
                        border: '2px solid #52B788',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                      }}>
                        <Star sx={{ color: '#52B788', mt: '4px', fontSize: 24, flexShrink: 0 }} />
                        <Typography sx={{ color: '#0f172a', fontWeight: 600, fontSize: '0.95rem', lineHeight: 1.6 }}>
                          "Every document has a story. We ensure yours is legally protected and properly documented."
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Box 2: Precision & Accuracy */}
                    <Grid item xs={12}>
                      <Box sx={{
                        p: 2.5,
                        borderRadius: 2,
                        background: '#ffffff',
                        border: '2px solid #52B788',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                      }}>
                        <Star sx={{ color: '#52B788', mt: '4px', fontSize: 24, flexShrink: 0 }} />
                        <Typography sx={{ color: '#0f172a', fontWeight: 600, fontSize: '0.95rem', lineHeight: 1.6 }}>
                          ✓ Precision & Accuracy in every legal document—ensuring compliance and peace of mind.
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Box 3: Legal Partner */}
                    <Grid item xs={12}>
                      <Box sx={{
                        p: 2.5,
                        borderRadius: 2,
                        background: '#ffffff',
                        border: '2px solid #52B788',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                      }}>
                        <Star sx={{ color: '#52B788', mt: '4px', fontSize: 24, flexShrink: 0 }} />
                        <Typography sx={{ color: '#0f172a', fontWeight: 600, fontSize: '0.95rem', lineHeight: 1.6 }}>
                          ⚖️ Your Legal Partner—trusted expertise for registrations, agreements, and all documentation needs.
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <PersonCard
                  name="Services Available:"
                  subtitle=""
                  accent="#52B788"
                  services={VIJAY_SERVICES}
                  searchTerm={query}
                  onServiceClick={handleServiceClick}
                  mode={mode}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Professional Highlight */}
          <motion.div variants={fadeUp} initial="initial" whileInView="animate" viewport={{ once: true, amount: 0.2 }}>
            <Box
              sx={{
                mt: 6,
                p: { xs: 3, md: 4 },
                borderRadius: 3,
                background: "linear-gradient(135deg, rgba(82,183,136,0.08) 0%, rgba(149,213,178,0.08) 100%)",
                border: "1px solid rgba(82,183,136,0.2)",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  textAlign: "center",
                  color: "#2D6A4F",
                  fontWeight: 600,
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  lineHeight: 1.7,
                }}
              >
                🏢 <span style={{ color: "#52B788", fontWeight: 800 }}>Trusted Partners</span> for Your Legal & Financial Growth
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  textAlign: "center",
                  color: "#52B788",
                  mt: 2,
                  fontSize: { xs: "0.9rem", md: "1rem" },
                  fontWeight: 500,
                }}
              >
                Comprehensive solutions with professional expertise, integrity, and commitment to excellence
              </Typography>
            </Box>
          </motion.div>

          {/* Contact */}
          <motion.div variants={fadeUp} initial="initial" whileInView="animate" viewport={{ once: true, amount: 0.2 }}>
            <Box
              id="contact"
              sx={{
                p: { xs: 4, md: 5 },
                background: "linear-gradient(135deg, rgba(82,183,136,0.16) 0%, rgba(149,213,178,0.16) 100%)",
                borderRadius: 4,
                scrollMarginTop: "88px",
                mb: 6,
              }}
              role="contentinfo"
              aria-label="Contact information"
            >
              <Typography
                variant="h4"
                gutterBottom
                sx={{
                  fontWeight: 800,
                  mb: 4,
                  textAlign: "center",
                  background: mode === "dark"
                    ? "linear-gradient(135deg, #2D6A4F 0%, #52B788 100%)"
                    : "linear-gradient(135deg, #2D6A4F 0%, #52B788 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Get In Touch
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: { xs: 2.5, sm: 3.5 },
                      borderRadius: 3,
                      background: "rgba(255,255,255,0.95)",
                      border: "1px solid rgba(82,183,136,0.40)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        borderColor: "#52B788",
                        boxShadow: "0 10px 32px rgba(82,183,136,0.40)",
                      },
                    }}
                    aria-label="Phone Numbers"
                  >
                    <Box sx={{ display: "flex", alignItems: { xs: "flex-start", sm: "center" }, gap: { xs: 1.5, sm: 2.5 }, flexDirection: { xs: "column", sm: "row" } }}>
                      <Box sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2, background: "rgba(82,183,136,0.20)", alignSelf: { xs: "flex-start", sm: "auto" } }} aria-hidden>
                        <Phone sx={{ fontSize: { xs: 24, sm: 28 }, color: "#52B788" }} />
                      </Box>
                      <Box sx={{ flex: 1, width: "100%" }}>
                        <Typography variant="body2" sx={{ color: "#52B788", fontWeight: 600, mb: 1 }}>
                          Phone Numbers
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: { xs: 0.5, sm: 1 }, flexWrap: "wrap" }}>
                          <Typography
                            component="a"
                            href="tel:+917989442456"
                            sx={{
                              color: "#2D6A4F",
                              fontWeight: 600,
                              fontSize: { xs: "0.9rem", sm: "1rem" },
                              textDecoration: "none",
                              display: "block",
                              "&:hover": {
                                color: "#52B788",
                                textDecoration: "underline",
                              },
                            }}
                          >
                            +917989442456
                          </Typography>
                          <Typography
                            sx={{
                              display: { xs: "none", sm: "block" },
                              color: "#52B788",
                              fontWeight: 600,
                              mx: 0.5,
                            }}
                          >
                            |
                          </Typography>
                          <Typography
                            component="a"
                            href="tel:+918374545326"
                            sx={{
                              color: "#2D6A4F",
                              fontWeight: 600,
                              fontSize: { xs: "0.9rem", sm: "1rem" },
                              textDecoration: "none",
                              display: "block",
                              "&:hover": {
                                color: "#52B788",
                                textDecoration: "underline",
                              },
                            }}
                          >
                            +918374545326
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: { xs: 2.5, sm: 3.5 },
                      borderRadius: 3,
                      background: "rgba(255,255,255,0.95)",
                      border: "1px solid rgba(82,183,136,0.40)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        borderColor: "#52B788",
                        boxShadow: "0 10px 32px rgba(82,183,136,0.40)",
                      },
                    }}
                    aria-label="Email Addresses"
                  >
                    <Box sx={{ display: "flex", alignItems: { xs: "flex-start", sm: "center" }, gap: { xs: 1.5, sm: 2.5 }, flexDirection: { xs: "column", sm: "row" } }}>
                      <Box sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2, background: "rgba(82,183,136,0.20)", alignSelf: { xs: "flex-start", sm: "auto" } }} aria-hidden>
                        <Email sx={{ fontSize: { xs: 24, sm: 28 }, color: "#52B788" }} />
                      </Box>
                      <Box sx={{ flex: 1, width: "100%" }}>
                        <Typography variant="body2" sx={{ color: "#52B788", fontWeight: 600, mb: 1 }}>
                          Email Us
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: { xs: 0.5, sm: 1 }, flexWrap: "wrap" }}>
                          <Typography
                            component="a"
                            href="mailto:LNSTAXSERVICES08@GMAIL.COM"
                            sx={{
                              color: "#2D6A4F",
                              fontWeight: 600,
                              fontSize: { xs: "0.85rem", sm: "0.95rem" },
                              textDecoration: "none",
                              wordBreak: "break-all",
                              display: "block",
                              "&:hover": {
                                color: "#52B788",
                                textDecoration: "underline",
                              },
                            }}
                          >
                            LNSTAXSERVICES08@GMAIL.COM
                          </Typography>
                          <Typography
                            sx={{
                              display: { xs: "none", sm: "block" },
                              color: "#52B788",
                              fontWeight: 600,
                              mx: 0.5,
                            }}
                          >
                            |
                          </Typography>
                          <Typography
                            component="a"
                            href="mailto:LNSLEGALSERVICES99@GMAIL.COM"
                            sx={{
                              color: "#2D6A4F",
                              fontWeight: 600,
                              fontSize: { xs: "0.85rem", sm: "0.95rem" },
                              textDecoration: "none",
                              wordBreak: "break-all",
                              display: "block",
                              "&:hover": {
                                color: "#52B788",
                                textDecoration: "underline",
                              },
                            }}
                          >
                            LNSLEGALSERVICES99@GMAIL.COM
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: { xs: 2.5, sm: 3.5 },
                      borderRadius: 3,
                      background: "rgba(255,255,255,0.95)",
                      border: "1px solid rgba(82,183,136,0.40)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        borderColor: "#52B788",
                        boxShadow: "0 10px 32px rgba(82,183,136,0.40)",
                      },
                    }}
                    aria-label="Office Address"
                  >
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: { xs: 1.5, sm: 2.5 }, flexDirection: { xs: "column", sm: "row" } }}>
                      <Box sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2, background: "rgba(82,183,136,0.20)", alignSelf: { xs: "flex-start", sm: "auto" } }} aria-hidden>
                        <LocationOn sx={{ fontSize: { xs: 24, sm: 28 }, color: "#52B788" }} />
                      </Box>
                      <Box sx={{ flex: 1, width: "100%" }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: mode === "dark" ? "text.secondary" : "#475569", 
                            lineHeight: { xs: 1.6, sm: 1.8 }, 
                            mb: 2,
                            fontSize: { xs: "0.9rem", sm: "1rem" },
                            wordBreak: "break-word",
                          }}
                        >
                          {address}
                        </Typography>
                        <Link
                          href={googleMapsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: mode === "dark" ? "#52B788" : "#2D6A4F",
                            fontWeight: 600,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 1,
                            textDecoration: "none",
                            fontSize: { xs: "0.9rem", sm: "1rem" },
                            "&:hover": { 
                              color: "#52B788",
                              textDecoration: "underline" 
                            },
                          }}
                          aria-label="View on Google Maps"
                        >
                          View on Google Maps <ArrowForward sx={{ fontSize: { xs: 14, sm: 16 } }} />
                        </Link>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </motion.div>

          {/* CTA */}
          <motion.div variants={fadeUp} initial="initial" whileInView="animate" viewport={{ once: true, amount: 0.2 }}>
            <Box
              sx={{
                p: { xs: 4, md: 6 },
                textAlign: "center",
                background: mode === "dark"
                  ? "rgba(5,5,8,0.70)"
                  : "linear-gradient(135deg, rgba(37,99,235,0.08) 0%, rgba(14,165,233,0.08) 100%)",
                borderRadius: 4,
                mb: 5,
                border: mode === "dark" ? "1px solid rgba(148,163,184,0.16)" : "1px solid rgba(37,99,235,0.12)",
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  mb: 2,
                  color: mode === "dark" ? "#E2E8F0" : "#1f2937",
                }}
              >
                Ready to experience seamless documentation?
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  maxWidth: 620,
                  mx: "auto",
                  mb: 4,
                  color: mode === "dark" ? "#A1AFC9" : "#475569",
                }}
              >
                Book a consultation slot and our experts will guide you through every requirement with clarity and confidence.
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={handleBooking}
                startIcon={<CalendarToday />}
                endIcon={<ArrowForward />}
                sx={{ py: 3, px: 8, fontSize: "1.25rem", fontWeight: 800 }}
                aria-label="Book your service slot"
              >
                Book Your Service Slot
              </Button>
              <Typography variant="body2" sx={{ mt: 3, color: "text.secondary", fontStyle: "italic" }}>
                Schedule your consultation today • Fast & Reliable Service
              </Typography>
            </Box>
          </motion.div>

          {/* Footer */}
          <Box
            sx={{
              textAlign: "center",
              p: 3,
              borderRadius: 3,
              background: "rgba(255,255,255,0.95)",
              border: "1px solid rgba(0,0,0,0.08)",
            }}
            component="footer"
          >
            <Typography
              variant="body2"
              sx={{
                color: mode === "dark" ? "#A1AFC9" : "#475569",
                fontWeight: 600,
              }}
            >
              © 2025 L.N.S. & ASSOCIATES | All Rights Reserved
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Sticky mobile action bar (kept) */}
      <MobileActionBar
        onBook={handleBooking}
        onCall1={() => call("+917989442456")}
        onCall2={() => call("+918374545326")}
        onWhatsApp1={() => toWhatsApp("917989442456", "Hi, I'd like to discuss services.")}
        onWhatsApp2={() => toWhatsApp("918374545326", "Hi, I'd like to discuss services.")}
        onMap={() => window.open(googleMapsUrl, "_blank")}
        mode={mode}
      />

      {/* Back to top */}
      {showTop && (
        <Fab
          color="secondary"
          aria-label="Back to top"
          onClick={scrollTop}
          sx={{ position: "fixed", right: 16, bottom: { xs: 136, md: 24 }, zIndex: 1300 }}
        >
          <ArrowUpward />
        </Fab>
      )}
    </>
  );
}
